<template>
  <div class="purchase-by-product">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown v-model="filters.sub_category" :options="subCategoryOptions" placeholder="All subcategories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search item, code or SKU" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-package"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Net Purchase Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.net_cost) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.product_count, 'product') }} · {{ plural(summary.invoice_count, 'invoice line') }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-packages"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Quantity Received</div>
          <div class="rp-card-value">{{ formatQty(summary.qty) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.net_qty) }} kept after returns</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-tag"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Average Unit Cost</div>
          <div class="rp-card-value">{{ formatMoney(summary.avg_cost) }}</div>
          <div class="rp-card-sub">gross cost {{ formatMoney(summary.cost) }} · VAT {{ formatMoney(summary.vat) }}</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: returnedOnly }"
              :title="returnedOnly ? 'Show all products' : 'Show products returned to suppliers'"
              @click="returnedOnly = !returnedOnly">
        <span class="rp-card-icon"><i class="ti ti-truck-return"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returned</div>
          <div class="rp-card-value">{{ formatMoney(summary.returned_value) }}</div>
          <div class="rp-card-sub">{{ formatQty(summary.returned_qty) }} units · {{ summary.return_rate }}% of cost</div>
        </div>
      </button>
    </div>

    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Top products by net cost</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: PURCHASE_COLOR }"></i>Net cost</span>
          </span>
        </div>
        <div v-show="summary.top_products.length" class="rp-chart" :style="{ height: `${chartHeight}px` }">
          <canvas ref="topChart"></canvas>
        </div>
        <p v-if="!summary.top_products.length" class="text-muted small mb-0">No purchases in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Cost by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(category.net_cost) }} <span class="text-muted fw-normal">· {{ formatQty(category.qty) }}</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill info" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highlights</div>
        <ul v-if="highlights.length" class="rp-highlights">
          <li v-for="item in highlights" :key="item.label" :class="item.tone">
            <span class="hl-icon"><i class="ti" :class="item.icon"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">{{ item.label }}</small>
              <div class="fw-semibold text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ item.detail }}</small>
            </div>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-package me-1"></i>Products
          <span class="rp-pill-count ms-1">{{ formatInt(visibleRows.length) }}</span>
          <span v-if="returnedOnly" class="rp-hint ms-2">
            Returned to suppliers only — <a href="#" @click.prevent="returnedOnly = false">show all</a>
          </span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td>
                <div class="fw-medium">{{ row.item_name }}</div>
                <span class="rp-sub">{{ row.item_code }}<template v-if="row.category"> · {{ row.category }}</template></span>
                <span v-for="part in variantParts(row)" :key="part" class="rp-variant mt-1">{{ part }}</span>
              </td>
              <td class="text-end fw-semibold">
                {{ formatQty(row.qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
                <span class="rp-sub">{{ plural(row.batches, 'batch') }}</span>
              </td>
              <td class="text-end" :class="Number(row.returned_qty) ? 'text-info' : 'text-muted'">
                {{ Number(row.returned_qty) ? formatQty(row.returned_qty) : '—' }}
                <span v-if="Number(row.returned_value)" class="rp-sub">{{ formatMoney(row.returned_value) }}</span>
              </td>
              <td class="text-end">{{ formatMoney(row.avg_cost) }}</td>
              <td class="text-end text-nowrap">
                <template v-if="row.min_cost !== null">
                  <span v-if="row.min_cost === row.max_cost">{{ formatMoney(row.min_cost) }}</span>
                  <template v-else>{{ formatMoney(row.min_cost) }} – {{ formatMoney(row.max_cost) }}</template>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end">
                <template v-if="row.last_cost !== null">
                  {{ formatMoney(row.last_cost) }}
                  <span class="rp-sub">{{ formatDate(row.last_date) }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-end">
                <div class="fw-semibold">{{ formatMoney(row.net_cost) }}</div>
                <span class="rp-sub">{{ row.share }}% of total</span>
              </td>
              <td>
                <span class="rp-count" :class="{ zero: !row.supplier_count }">{{ row.supplier_count }}</span>
                <span class="rp-sub text-truncate pp-suppliers" :title="row.suppliers.join(', ')">
                  {{ row.last_supplier || row.suppliers[0] || '—' }}
                </span>
              </td>
              <td class="text-end">
                <template v-if="row.markup !== null">
                  <span class="rp-badge" :class="marginTone(row.markup)">{{ row.markup }}%</span>
                  <span class="rp-sub">sells {{ formatMoney(row.selling_price) }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-package"></i>
                  <div class="fw-semibold">No purchases found</div>
                  <small class="text-muted">Try another period, supplier or category.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th class="text-end">Total ({{ plural(visibleRows.length, 'product') }})</th>
              <th class="text-end">{{ formatQty(footer.qty) }}</th>
              <th class="text-end">{{ formatQty(footer.returned_qty) }}</th>
              <th class="text-end">{{ formatMoney(footer.qty ? footer.cost / footer.qty : 0) }}</th>
              <th colspan="2"></th>
              <th class="text-end">{{ formatMoney(footer.net_cost) }}</th>
              <th colspan="2"></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Net cost = purchase cost less what went back to suppliers in the period. Avg cost = cost ÷ quantity received;
      markup compares the current selling price with it.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { marginTone } from '../sales/salesReportMixin';
import purchaseReport, { PURCHASE_COLOR } from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, cost: 0, discount: 0, vat: 0, total: 0, returned_qty: 0, returned_value: 0, net_qty: 0, net_cost: 0,
  product_count: 0, invoice_count: 0, return_rate: 0, avg_cost: 0, top_products: [], by_category: [],
  most_bought: null, most_suppliers: null, widest_range: null,
};
const SUM_FIELDS = ['qty', 'cost', 'returned_qty', 'net_cost'];

export default {
  name: 'PurchaseByProductReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.purchase_by_product_report.get,
  fileName: 'purchase_by_product',
  columns: [
    { key: 'item_name', label: 'Product' },
    { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
    { key: 'returned_qty', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'avg_cost', label: 'Avg Cost', class: 'text-end', numeric: true },
    { key: 'max_cost', label: 'Cost Range', class: 'text-end', numeric: true },
    { key: 'last_cost', label: 'Last Cost', class: 'text-end', numeric: true },
    { key: 'net_cost', label: 'Net Cost', class: 'text-end', numeric: true },
    { key: 'supplier_count', label: 'Suppliers', numeric: true },
    { key: 'markup', label: 'Markup', class: 'text-end', numeric: true },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      returnedOnly: false,
      sortKey: null,
      sortDir: 'desc',
      PURCHASE_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.supplier || this.returnedOnly;
    },

    chartHeight() {
      return Math.max(170, this.summary.top_products.length * 26 + 30);
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'net_cost');
    },

    highlights() {
      const s = this.summary;
      return [
        s.most_bought && {
          label: 'Most bought', tone: 'tone-orange', icon: 'ti-packages', name: s.most_bought.name,
          detail: `${this.formatQty(s.most_bought.value)} units · ${this.formatMoney(s.most_bought.net_cost)}`,
        },
        s.most_suppliers && Number(s.most_suppliers.value) > 1 && {
          label: 'Most suppliers', tone: 'tone-blue', icon: 'ti-truck', name: s.most_suppliers.name,
          detail: `bought from ${this.plural(s.most_suppliers.value, 'supplier')}`,
        },
        s.widest_range && Number(s.widest_range.spread) > 0 && {
          label: 'Widest cost range', tone: 'tone-red', icon: 'ti-arrows-diff', name: s.widest_range.name,
          detail: `${this.formatMoney(s.widest_range.min_cost)} – ${this.formatMoney(s.widest_range.max_cost)} (${s.widest_range.spread}% apart)`,
        },
        Number(s.returned_value) > 0 && {
          label: 'Returned to suppliers', tone: 'tone-teal', icon: 'ti-truck-return',
          name: this.formatMoney(s.returned_value), detail: `${s.return_rate}% of the purchase cost`,
        },
      ].filter(Boolean);
    },

    visibleRows() {
      const rows = this.returnedOnly ? this.rows.filter((r) => Number(r.returned_qty) > 0) : this.rows;
      return this.sortKey ? this.sortRows(rows) : rows;
    },

    footer() {
      return Object.fromEntries(SUM_FIELDS.map((f) => [f, this.visibleRows.reduce((s, r) => s + (Number(r[f]) || 0), 0)]));
    },
  },

  watch: {
    returnedOnly() { this.page = 1; },
  },

  methods: {
    marginTone,

    extraParams() {
      return { ...this.periodParams(), ...this.supplierParams() };
    },

    resetExtraFilters() {
      this.sortKey = null;
      this.returnedOnly = false;
      return { ...defaultPeriod(this.today), supplier: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const top = this.summary.top_products;
      this.$nextTick(() => this.drawBars('top', this.$refs.topChart, top.map((p) => p.name), [
        { label: 'Net cost', data: top.map((p) => p.net_cost), color: PURCHASE_COLOR },
      ], { horizontal: true }));
    },

    variantParts(row) {
      return row.variant ? row.variant.split(' · ') : [];
    },

    printReport() {
      const s = this.summary;
      const f = this.footer;
      this.printColumns(
        'Purchase by Product',
        [this.periodText(), this.supplierText(), ...this.itemFiltersText(), this.returnedOnly && 'Returned only'],
        [
          ['Net Cost', `${this.formatMoney(s.net_cost)} · ${this.plural(s.product_count, 'product')}`],
          ['Quantity', `${this.formatQty(s.qty)} · ${this.formatQty(s.returned_qty)} returned`],
          ['Average Cost', this.formatMoney(s.avg_cost)],
          ['Returned', `${this.formatMoney(s.returned_value)} (${s.return_rate}%)`],
        ],
        [
          { label: 'Item Code', value: (r) => r.item_code },
          { label: 'Product', value: (r) => `${r.item_name}${r.variant ? ` (${r.variant})` : ''}` },
          { label: 'Category', value: (r) => r.category },
          { label: 'Qty', value: (r) => this.formatQty(r.qty), num: true },
          { label: 'Returned', value: (r) => this.formatQty(r.returned_qty), num: true },
          { label: 'Avg Cost', value: (r) => this.formatMoney(r.avg_cost), num: true },
          { label: 'Low', value: (r) => this.formatMoney(r.min_cost), num: true },
          { label: 'High', value: (r) => this.formatMoney(r.max_cost), num: true },
          { label: 'Last Cost', value: (r) => this.formatMoney(r.last_cost), num: true },
          { label: 'Net Cost', value: (r) => this.formatMoney(r.net_cost), num: true },
          { label: 'Suppliers', value: (r) => r.suppliers.join(', ') },
        ],
        this.visibleRows,
        {
          Category: 'Total', Qty: this.formatQty(f.qty), Returned: this.formatQty(f.returned_qty),
          'Avg Cost': this.formatMoney(f.qty ? f.cost / f.qty : 0), 'Net Cost': this.formatMoney(f.net_cost),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.pp-suppliers {
  max-width: 160px;
}
</style>
